<script setup lang="ts">
  import type { TInternalLanguage } from '@/utils/constants/locale'
  import { useLanguagesStore } from '@/stores/Languages.store'
  import { useLocaleStore } from '@/stores/Locale.store'
  import { useCountriesStore } from '@/stores/Countries.store'
  import { usePageStore } from '@/stores/Page.store'
  import { useDynamicTranslation } from '@/composables/useDynamicTranslation'
  import { storeToRefs } from 'pinia'
  const { locale } = useI18n()
  const { translate } = useDynamicTranslation()
  const switchLocalePath = useSwitchLocalePath()

  const languageStore = useLanguagesStore()
  const localeStore = useLocaleStore()
  const countryStore = useCountriesStore()
  const pageStore = usePageStore()

  const { availableLanguages } = storeToRefs(languageStore)
  const { selectedLanguage, selectedCountry, selectedCountrySameAsCookie } = storeToRefs(localeStore)
  const { availableCountries } = storeToRefs(countryStore)

  countryStore.getCountriesFromAPI()

  const isLanguageExternal = (languageId: TInternalLanguage) => {
    return availableLanguages.value.find((item) => item.id === languageId)?.external
  }

  const isPopoverOpen = ref(false)
  const isCountrySelectOpen = ref(false)
  const isLanguageSelectOpen = ref(false)

  const isSelectOpen = computed(() => isCountrySelectOpen.value || isLanguageSelectOpen.value)

  const setLocalization = async () => {
    if (!selectedCountrySameAsCookie.value) {
      localeStore.setSelectedCountry(selectedCountry.value)
      isPopoverOpen.value = false
    }

    if (locale.value === selectedLanguage.value) {
      return
    }

    const external = isLanguageExternal(selectedLanguage.value)

    if (external) {
      return navigateTo(external, { external: true })
    }

    const langPath = pageStore.slugs?.filter((slug) => slug.lang === selectedLanguage.value)
    const [pagePath] = langPath || []

    const path = pagePath?.path
      ? `/${selectedLanguage.value}/${pagePath.path}`
      : switchLocalePath(selectedLanguage.value)

    reloadNuxtApp({ path, force: true })
  }

  const resetUserSelection = (value: boolean | undefined) => {
    if (!value) {
      localeStore.resetSelectedLanguageCountry()
    }
  }
</script>
<template>
  <div>
    <UiPopover
      v-model="isPopoverOpen"
      has-overlay
      :label="$t('language.select')"
      @update:model-value="resetUserSelection"
    >
      <template #trigger>
        <div class="inline-flex size-10 cursor-pointer items-center justify-center rounded-full bg-white">
          <Icon name="common/language" />
        </div>
      </template>
      <template #content>
        <div
          class="fixed flex h-full w-full flex-col gap-5 bg-white p-5 pt-20 sm:relative sm:h-auto sm:min-w-90 sm:rounded sm:pt-5"
        >
          <UiSelect
            v-model="selectedCountry"
            v-model:open="isCountrySelectOpen"
            label="country.select"
            disable-item-translation
            name="Country"
            :items="availableCountries"
          />

          <UiSelect
            v-model="selectedLanguage"
            v-model:open="isLanguageSelectOpen"
            label="language.select"
            disable-item-translation
            name="Langauge"
            :items="availableLanguages"
          />
          <div>
            <LazyUiButton
              class="w-full font-medium"
              :title="translate('save')"
              variant="primary"
              @click="setLocalization"
            />
          </div>
        </div>
      </template>
      <template #close>
        <div
          class="absolute left-5 top-5 z-10 flex size-6 cursor-pointer"
          :class="{ hidden: isSelectOpen }"
        >
          <Icon name="common/close" />
        </div>
        <div
          class="absolute left-0 top-0 h-16 w-full border-b bg-white"
          :class="{ hidden: isSelectOpen }"
        />
      </template>
    </UiPopover>
    <UiDarkOverlay
      v-if="isPopoverOpen"
      class="inset-0"
    />
  </div>
</template>
